/**
 * CCTV detection_scenario / 시설 프로필 → 수동이벤트 유형 목록.
 * 백엔드 detection_scenario_catalog analyzers 와 동기.
 */

import {
  CCTV_DETECTION_SCENARIO_OPTIONS,
  DEFAULT_DETECTION_SCENARIO,
  PROFILE_SCENARIO_CODES,
  normalizeScenarioProfile,
  resolveDetectionForFacilityProfile,
  type ScenarioProfile,
} from './cctv';
import { resolveRiskAppearance } from './riskEventAppearance';

export type ManualRiskEventOption = { type: string; label: string };

/** scenario code → analyzer codes (backend catalog) */
export const SCENARIO_ANALYZERS: Record<string, readonly string[]> = {
  pool_drowning: ['drown_motion'],
  water_play: ['loitering', 'crowd_density'],
  deck_slip_fall: ['slip_fall', 'fallen_immobile'],
  walkway_collision: ['collision_person'],
  pool_deck_mixed: ['drown_motion', 'slip_fall', 'fallen_immobile'],
  deck_safety: ['slip_fall', 'fallen_immobile', 'collision_person'],
  walkway_safety: ['collision_person', 'slip_fall', 'fallen_immobile'],
  abnormal_behavior: ['loitering', 'intrusion'],
  venue_crowd: ['crowd_density'],
  venue_stampede: ['crowd_stampede', 'crowd_density'],
  school_campus: ['crowd_density', 'loitering', 'intrusion'],
  worship_venue: ['crowd_density', 'loitering'],
  perimeter_security: ['intrusion', 'loitering'],
  parking_vehicle: ['collision_vehicle', 'near_miss'],
  industrial_ppe: ['ppe_violation'],
  industrial_proximity: ['near_miss'],
  industrial_fall: ['slip_fall', 'fall_height'],
  fire_smoke: ['fire_smoke'],
  health_fallen: ['fallen_immobile'],
};

/** analyzer → 수동/표시용 risk type 코드들 */
const ANALYZER_RISK_TYPES: Record<string, readonly string[]> = {
  drown_motion: ['drowning', 'no_motion', 'sinking'],
  slip_fall: ['slip_fall'],
  fallen_immobile: ['fallen_immobile'],
  collision_person: ['collision_person'],
  loitering: ['loitering'],
  intrusion: ['intrusion'],
  crowd_density: ['crowd_density'],
  crowd_stampede: ['crowd_stampede'],
  near_miss: ['near_miss'],
  fall_height: ['fall_height'],
  fire_smoke: ['fire_smoke'],
  ppe_violation: ['ppe_violation'],
  collision_vehicle: ['collision_vehicle'],
};

const FALLBACK_DROWN: ManualRiskEventOption[] = [
  { type: 'drowning', label: '익사' },
  { type: 'no_motion', label: '움직임없음' },
  { type: 'sinking', label: '가라앉음' },
];

function optionForType(type: string): ManualRiskEventOption {
  const app = resolveRiskAppearance(type);
  return { type, label: app.label || type };
}

export function riskTypesForAnalyzers(analyzers: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of analyzers) {
    const types = ANALYZER_RISK_TYPES[a] || [a];
    for (const t of types) {
      if (seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

export function manualRiskEventsForScenario(
  scenarioCode: string | null | undefined
): ManualRiskEventOption[] {
  const code = String(scenarioCode || '').trim();
  if (!code) {
    return FALLBACK_DROWN.map((e) => optionForType(e.type));
  }
  const analyzers = SCENARIO_ANALYZERS[code];
  if (!analyzers || analyzers.length === 0) {
    // 알 수 없는 코드면 익사 고정하지 않고 프로필 쪽으로 넘기도록 빈 목록
    return [];
  }
  return riskTypesForAnalyzers(analyzers).map(optionForType);
}

/** 시설 프로필에 허용된 시나리오들의 유형 합집합 */
export function manualRiskEventsForProfile(
  profile: string | null | undefined
): ManualRiskEventOption[] {
  const p = normalizeScenarioProfile(profile);
  const allowed =
    (PROFILE_SCENARIO_CODES as Record<string, readonly string[] | null>)[p as ScenarioProfile] ??
    null;
  const codes =
    allowed == null
      ? CCTV_DETECTION_SCENARIO_OPTIONS.filter((o) => o.phase === 'active').map((o) => o.value)
      : [...allowed];
  const seen = new Set<string>();
  const out: ManualRiskEventOption[] = [];
  for (const sc of codes) {
    for (const ev of manualRiskEventsForScenario(sc)) {
      if (seen.has(ev.type)) continue;
      seen.add(ev.type);
      out.push(ev);
    }
  }
  if (out.length > 0) return out;
  // 프로필 기본 시나리오 한 개라도
  const def = resolveDetectionForFacilityProfile(p).scenario || DEFAULT_DETECTION_SCENARIO;
  const one = manualRiskEventsForScenario(def);
  return one.length > 0 ? one : FALLBACK_DROWN.map((e) => optionForType(e.type));
}

/**
 * 수동이벤트 목록:
 * - CCTV 시나리오가 시설 프로필에 허용되면 그 시나리오 유형
 * - 아니면 시설 관제 프로필 유형 (수영장 익사 고정 방지)
 */
export function resolveManualRiskEvents(opts: {
  scenarioProfile?: string | null;
  selectedCctvScenario?: string | null;
}): ManualRiskEventOption[] {
  const profile = normalizeScenarioProfile(opts.scenarioProfile);
  const selected = String(opts.selectedCctvScenario || '').trim();
  if (selected) {
    const allowed =
      (PROFILE_SCENARIO_CODES as Record<string, readonly string[] | null>)[profile] ?? null;
    const ok = allowed == null || allowed.includes(selected);
    if (ok) {
      const fromSc = manualRiskEventsForScenario(selected);
      if (fromSc.length > 0) return fromSc;
    }
  }
  return manualRiskEventsForProfile(profile);
}
